import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  IAbility,
  IModalVisible,
  ISupportLanguage,
} from 'utils/types/storyTypes';
import { storyApi } from 'apis/story/storyApis';
import DefaultLayout from 'components/layout/DefaultLayout';
import { useAppDispatch } from 'hooks/redux';
import { Checkbox, message } from 'antd';
import { setEditName, setStoryName } from 'store/slices/breadcumbSlice';
import { useCookies } from 'react-cookie';
import { charactersApis } from 'apis/characters/charactersApis';
import InputWrappingBox from 'components/container/InputWrappingBox';
import InputStyled from 'components/InputStyled';
import { theme } from 'utils/style/theme';
import TwoColumnLocalizeSupporter from 'components/LocalizeSupportTable/TwoColumLocalizeSuppporter';
import UploadSingleImage from 'components/ImageUploader/UploadSingleImage';
import PrimaryButton from 'components/button/PrimaryButton';
import SubmitFlexBox from 'components/container/SubmitFlexBox';
import {
  showApiErrorMessage,
  showApiSuccessMessage,
} from 'utils/helper/message';

const AbilityEditPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [cookies] = useCookies();

  const [currentAbility, setCurrentAbility] = useState<IAbility | null>(null);
  const [visibleState, setVisibleState] = useState<IModalVisible>({
    create: false,
    edit: false,
  });

  const {
    state: { ability },
  } = useLocation();

  const navigate = useNavigate();

  const { projectId, profile_id, ability_id } = useParams();

  useEffect(() => {
    setCurrentAbility(ability);

    dispatch(setEditName(currentAbility?.ability_name));
  }, []);

  // 값 변경
  const handleChange = (value, type) => {
    const cloneData = JSON.parse(JSON.stringify(currentAbility));
    cloneData[type] = value;

    setCurrentAbility(cloneData);
  };

  // 취소(뒤로가기)
  const onClickCancel = () => {
    navigate(-1);
  };

  const submit = async () => {
    if (!currentAbility) {
      return;
    }

    try {
      const result = await charactersApis.characterUpdateAbility(
        projectId,
        currentAbility.profile_id,
        currentAbility.ability_id,
        currentAbility,
      );

      // 변경된 profile 정보
      const updatedAbility: IAbility = result.update;

      setCurrentAbility(updatedAbility);

      showApiSuccessMessage('저장되었습니다');
    } catch (err) {
      showApiErrorMessage(err);
    }
  };

  return (
    <>
      <DefaultLayout>
        <InputWrappingBox>
          <Wrapper>
            <SectionWarpper>
              <div className='header'>
                <p>대상 능력 : {ability_id}</p>
              </div>
              <div>
                <p className='disabled-text'>능력 이름</p>
                <InputStyled
                  defaultValue={currentAbility?.ability_name}
                  disabled
                />
              </div>
              <TwoColumnLocalizeSupporter
                targetData={currentAbility}
                setTargetData={setCurrentAbility}
                visibleState={visibleState}
                setVisibleState={setVisibleState}
                currentLocalDataArray={currentAbility?.localizations}
                targetLocalize='ability'
                columnName='능력명'
              />
              <CheckboxSection>
                <CheckboxWrapper>
                  <p className='title'>주 능력 여부</p>
                  <CheckboxStyled
                    checked={currentAbility?.is_main}
                    onChange={(e) => {
                      handleChange(e.target.checked, 'is_main');
                    }}
                  />
                </CheckboxWrapper>
              </CheckboxSection>
              <div className='input-section-box'>
                <p className='title'>선물하기로 얻을 수 있는 수치 상한선</p>
                <InputStyled
                  value={currentAbility?.gift_limit_value}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    handleChange(e.target.value, 'gift_limit_value');
                  }}
                  type='number'
                />
              </div>
              <div className='input-section-box'>
                <p className='title'>능력 수치 상한선</p>
                <InputStyled
                  value={currentAbility?.max_value}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    handleChange(e.target.value, 'max_value');
                  }}
                  type='number'
                />
              </div>
              <p className='title'>능력 아이콘 이미지 등록</p>
              <UploadSingleImage
                targetData={currentAbility}
                setTargetData={setCurrentAbility}
                projectId={projectId}
                image_type='icon'
                api_target='ability'
              />
            </SectionWarpper>
          </Wrapper>
        </InputWrappingBox>
        <SubmitFlexBox style={{ columnGap: 12, marginTop: '24px' }}>
          <>
            <PrimaryButton
              text={'뒤로가기'}
              height='40px'
              width='151px'
              bgColor='#FFFF'
              textColor={theme.colors.dark_400}
              fontWeight={theme.fontWeights.medium}
              fontSize={theme.fontSizes.normal}
              border={`1px solid ${theme.colors.gray_6}`}
              rounded='6px'
              hoverBgColor={'#FFFF'}
              activeBgColor={'#FFFF'}
              selectedBgColor={'#FFFF'}
              onClick={onClickCancel}
            />
            <PrimaryButton
              text={'저장'}
              height='40px'
              width='151px'
              bgColor={theme.colors.blue_500}
              textColor='#ffffff'
              fontWeight={theme.fontWeights.medium}
              fontSize={theme.fontSizes.normal}
              border={`1px solid ${theme.colors.blue_500}`}
              rounded='6px'
              hoverBgColor={theme.colors.blue_600}
              activeBgColor={theme.colors.blue_800}
              selectedBgColor={theme.colors.blue_700}
              onClick={submit}
            />
          </>
        </SubmitFlexBox>
      </DefaultLayout>
    </>
  );
};

export default AbilityEditPage;

const Wrapper = styled.div`
  & > * + * {
    margin-top: 8rem;
  }
  .ant-input-disabled {
    color: ${theme.colors.dark_300};
  }
  .input-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > * + * {
      margin-left: 8px;
    }
  }
  .input-section-box {
    width: 50%;
  }
`;

const SectionWarpper = styled.div`
  & > * + * {
    margin-top: 24px;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  & > * + * {
    margin-left: 1rem;
  }
`;

const CheckboxSection = styled.div`
  display: flex;
  & > * + * {
    margin-left: 24px;
  }
`;

const CheckboxStyled = styled(Checkbox)`
  .ant-checkbox .ant-checkbox-input {
    cursor: default;
  }
  .ant-checkbox:not(.ant-checkbox-disabled):hover .ant-checkbox-inner {
    border: 1px solid #d9d9d9;
  }
`;
